<template>
  <div class="content">
    <div class="page-inner">
      <h1 class="mb-4">
        AI Mastering <span class="badge-pro badge-primary bg-gradient text-white fw-bold px-2 py-1">PRO</span>
      </h1>

      <div id="accordion">
        <div class="card">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left" :class="{ 'text-primary': currentStep === 0, 'text-muted': currentStep === 0 }">
              {{ $t('choose_audio_file') }}
            </h4>
            <i class="pull-right fa-2x fa fa-check" :class="{ 'text-light': currentStep === 0, 'text-success animated bounceIn': currentStep > 0 }"></i>
          </div>

          <div class="collapse" :class="{ 'show': currentStep === 0 }">
            <div class="card-body">
              <div class="form-group">
                <label for="masteringFile">{{ $t('project_name') }}</label>
                <input type="text" v-model="master.name" class="form-control mt-2">
              </div>
              <div class="form-group">
                <label for="masteringFile">{{ $t('select_audio_file') }} (.mp3, .wav)</label>
                <input @change="loadAudioFile($event)" type="file" accept=".wav, .mp3" class="form-control-file mt-2" id="masteringFile">
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left" :class="{ 'text-primary': currentStep === 1, 'text-muted': currentStep <= 1 }">
              {{ $t('choose_preview_loop') }}
            </h4>
            <i class="pull-right fa-2x fa fa-check" :class="{ 'text-light': currentStep <= 1, 'text-success animated bounceIn': currentStep > 1 }"></i>
          </div>
          <div class="collapse" :class="{ show: currentStep === 1 }">
            <div class="card-body">

              <div class="form-group" v-if="selectedFile && !uploadingFile">
                <label for="">{{ $t('select_file') }}: </label>
                <div class="input-group">
                  <input type="text" readonly :value="selectedFile.name" class="form-control" placeholder="" aria-label="" aria-describedby="basic-addon1">
                  <div class="input-group-append">
                    <button class="btn btn-default" @click="replaceFile()" type="button">{{ $t('replace_file') }}</button>
                  </div>
                </div>
              </div>

              <div class="wave-container mt-3 p-3" v-if="selectedFile && !uploadingFile">
                <p>
                  {{ $t('select_seconds_loop') }}
                </p>
                <div ref="wavesurferContainer"></div>
                <div class="mt-3 text-right" v-show="selectedFile">
                  <button type="button" class="btn" @click="play()" v-if="!playing">
                    <i class="fa fa-play mr-2"></i> {{ $t('preview') }}
                  </button>
                  <button type="button" class="btn" @click="pause()" v-if="playing">
                    <i class="fa fa-pause mr-2"></i> {{ $t('pause')}}
                  </button>
                  <button type="button" class="btn btn-primary ml-2" @click="generatePreview()">
                    {{ $t('generate_preview') }}
                  </button>
                </div>
              </div>

              <div v-if="selectedFile">
                <div v-if="uploadingFile && !generatingPreviews">
                  <div><i class="fa fa-spinner fa-spin mr-2 text-muted"></i> {{ $t('uploading_file') }}</div>
                  <div class="progress mt-2">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" :style="`width: ${uploadProgress || 1 }%;`"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left" :class="{ 'text-primary': currentStep === 2, 'text-muted': currentStep <= 2 }">
              {{ $t('select_preferred_preset') }}
            </h4>
            <i class="pull-right fa-2x fa fa-check" :class="{ 'text-light': currentStep <= 2, 'text-success animated bounceIn': currentStep > 2 }"></i>
          </div>
          <div class="collapse" :class="{ show: currentStep === 2 }">
            <div class="card-body">
              <div v-if="master.job_status === 'Success'">
                <h4>{{ $t('choose_master_preset') }}</h4>
                <ul class="nav nav-pills nav-success nav-fill w-100" v-show="!downloadingPreview">
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: selectedPreset === 'a' }" @click="selectPreview('a')">Platinum Pulse</a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: selectedPreset === 'b' }" @click="selectPreview('b')">Sonic Brilliance</a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: selectedPreset === 'c' }" @click="selectPreview('c')">Vibe Clarity</a>
                  </li>
                </ul>
                <p v-if="downloadingPreview" class="mt-3">
                  <i class="fa fa-spinner fa-spin text-muted mr-2"></i> {{ $t('downloading_preview')}}
                </p>
                <div class="mt-3" v-show="selectedPreview && !downloadingPreview">
                  <audio controls class="preview-audio" v-if="selectedPreview">
                    <source :src="selectedPreview" type="audio/mpeg">
                  </audio>
                </div>
                <div class="mt-4 text-right" v-if="!downloadingPreview">
                  <button v-if="selectedPreset" type="button" class="btn btn-primary" @click="finishMastering()" :disabled="downloadingPreview">
                    {{ $t('process_final_with') }}: {{ presets[selectedPreset] }}
                  </button>
                </div>
              </div>
              <div v-if="master.job_id && master.job_status !== 'Success'">
                <i class="fa fa-spinner fa-spin text-muted mr-2"></i> {{ $t('generating_previews') }}
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header clearfix">
            <h4 class="mb-0 pull-left" :class="{ 'text-primary': currentStep === 3, 'text-muted': currentStep !== 3 }">
              {{ $t('download_mastered')}}
            </h4>
            <i class="pull-right fa-2x fa fa-check" :class="{ 'text-light': currentStep <= 3, 'text-success animated bounceIn': currentStep > 3 }"></i>
          </div>
          <div class="collapse" :class="{ show: currentStep === 3 }">
            <div class="card-body">
              <p>
                {{ $t('congratulations_mastering_finished') }}
              </p>
              <button type="button" class="btn btn-primary text-white" v-if="downloadingMaster" disabled>
                <span v-if="downloadingMaster || !master.mastered"><i class="fa fa-spinner fa-spin mr-2"></i> {{ $t('generating_download_link') }}</span>
              </button>
              <a :href="master.mastered" target="_blank" type="button" class="btn btn-primary text-white" v-if="!downloadingMaster">
                <span v-if="master.mastered">{{ $t('download') }}</span>
              </a>
              <router-link to="/tools/mastering" class="btn btn-light ml-2">
                {{ $t('back') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import _ from 'underscore'
import Upload from '../services/upload'
import Mastering from '../services/mastering'

export default {
  name: 'MasteringProcess',
  data: function () {
    return {
      presets: {
        a: 'Platinum Pulse',
        b: 'Sonic Brilliance',
        c: 'Vibe Clarity'
      },
      downloadingMaster: false,
      downloadingPreview: false,
      selectedPreset: '',
      previewStatus: '',
      currentPreview: '',
      generatingPreviews: false,
      uploadProgress: 0,
      uploadingFile: false,
      selectedPreview: null,
      currentStep: 0,
      selectedFile: null,
      playing: false,
      master: {},
      wavesurfer: null,
      activePage: true
    }
  },
  methods: {
    selectPreview: function (preview) {
      _.each(window.$('audio'), item => {
        item.pause()
      })

      this.selectedPreset = preview
      this.selectedPreview = null

      setTimeout(() => {
        if (!this.master[`preview_${preview}`]) {
          this.downloadingPreview = true
          Mastering.downloadPreview(this.master._id, preview).then(response => {
            this.selectedPreview = response.data
            this.downloadingPreview = false
          })
        } else {
          this.selectedPreview = this.master[`preview_${preview}`]
        }
      }, 1)
    },
    downloadMaster: function () {
      this.downloadingMaster = true

      Mastering.downloadMaster(this.master._id).then(response => {
        this.master.mastered = response.data
        this.downloadingMaster = false
      })
    },
    checkMaster: function (jobId) {
      if (!jobId || !this.activePage) return

      Mastering.checkMaster(jobId).then(response => {
        let status = response.data.status

        if (status !== 'Success') {
          setTimeout(() => {
            this.checkMaster(jobId)
          }, 1000 * 3)
        } else if (status === 'Success') {
          this.downloadMaster()
        }
      })
    },
    checkPreviews: function (jobId) {
      if (!jobId || !this.activePage) return

      Mastering.checkPreviews(jobId).then(response => {
        this.previewStatus = response.data.status
        this.master.job_status = this.previewStatus

        if (this.previewStatus !== 'Success') {
          setTimeout(() => {
            this.checkPreviews(jobId)
          }, 1000 * 3)
        } else if (this.previewStatus === 'Success') {
          this.$forceUpdate()
        }
      })
    },
    getMaster: function () {
      Mastering.get(this.$route.params.id).then(response => {
        this.master = response.data

        if (this.master.job_id && this.master.job_status !== 'Success') {
          this.checkPreviews(this.master.job_id)
        } else if (this.master.job_id && this.master.job_status === 'Success') {
          this.currentStep = 2
        }

        if ((this.master.master_job_id && this.master.master_job_status !== 'Success') || !this.master.mastered) {
          this.checkMaster(this.master.master_job_id)
        } else if (this.master.master_job_id && this.master.master_job_status === 'Success') {
          this.currentStep = 3
        }
      })
    },
    uploadFile: function (masterId) {
      let file = this.selectedFile
      this.uploadingFile = true

      return Upload.uploadMastering(masterId, file, event => {
        if (parseInt((event.loaded / event.total) * 100) > 0) {
          this.uploadProgress = parseInt((event.loaded / event.total) * 100)
          this.$forceUpdate()
        }
      }, masterId).catch(error => {
        this.uploadingFile = false

        if (error) {
          console.error('Error uploading track', error, error.response, file)
          this.$notify({ message: `${this.$t('there_was_an_error_uploading')} Track, ${this.$t('please_retry_again')}` }, 'warning', 'fa-exclamation')
        }
      })
    },
    finishMastering: function () {
      _.each(window.$('audio'), item => {
        item.pause()
      })

      this.currentStep = 3

      Mastering.finishMastering(this.master._id, this.selectedPreset).then(response => {
        this.master.master_job_id = response.data
        this.checkMaster(response.data)
      })
    },
    generatePreview: function () {
      this.master.preview_start = this.wavesurfer.regions.list['main'].start

      Mastering.save(this.master).then(response => {
        this.master = _.extend(this.master, response.data)
        this.pause()

        this.uploadFile(this.master._id).then(() => {
          this.$router.push(`/tools/mastering/${this.master._id}?step=2`)
        }).catch(() => {
          alert('There was an error generating the preview, please contact support@indiefy.net')
        })
      })
    },
    replaceFile: function () {
      this.selectedFile = null
      this.currentStep = 0
    },
    play: function () {
      this.playing = true
      this.wavesurfer.regions.list['main'].play()
    },
    pause: function () {
      this.wavesurfer.pause()
      this.playing = false
    },
    loadAudioFile: function (event) {
      this.selectedFile = event.target.files[0]
      this.currentStep = 1

      if (!this.master.name) {
        this.master.name = this.selectedFile.name
      }

      setTimeout(() => {
        this.wavesurfer = window.WaveSurfer.create({
          interact: false,
          container: this.$refs.wavesurferContainer,
          waveColor: 'lightblue',
          progressColor: '#007bff',
          plugins: [
            window.WaveSurfer.regions.create({
              regions: [
                {
                  id: 'main',
                  start: 0,
                  end: 30,
                  loop: false,
                  color: 'hsla(227, 100%, 50%, 0.1)'
                }
              ]
            })
          ]
        })

        this.wavesurfer.loadBlob(this.selectedFile)

        this.wavesurfer.regions.list['main'].on('play', () => {
          this.playing = true
        })

        this.wavesurfer.regions.list['main'].on('pause', () => {
          this.playing = false
        })

        this.wavesurfer.on('region-update-end', () => {
          if (this.playing) {
            this.pause()

            setTimeout(() => {
              this.play()
            }, 1)
          }
        })
      }, 1)
    }
  },
  mounted: function () {
  },
  beforeDestroy: function () {
    if (this.wavesurfer) {
      this.wavesurfer.destroy()
    }

    this.activePage = false
  },
  created: function () {
    this.user = this.$user

    if (this.$route.query.step) {
      this.currentStep = parseInt(this.$route.query.step)
    }

    if (this.$route.params.id !== 'new') {
      this.getMaster()
    }
  },
  watch: {
    $route: function () {
      if (this.$route.query.step) {
        this.currentStep = parseInt(this.$route.query.step)
      }

      if (this.$route.params.id !== 'new') {
        this.getMaster()
      }
    }
  },
  components: {
  }
}
</script>
<style lang="scss" scoped>
.preview-audio {
  width: 100%;
}

.badge-pro {
  border-radius: 3px;
}

.nav-item {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/MasteringProcess.vue